import { useMemo, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import type { DaySlots } from '@/data/mock';

const INITIAL_VISIBLE_DAYS = 2;
const INITIAL_VISIBLE_SLOTS = 6;

interface TimeSlotPickerProps {
  days: DaySlots[];
  selectedTime: { date: string; time: string } | null;
  onSelectTime: (date: string, time: string, label: string) => void;
  onLoadMore?: () => void;
  hasMore?: boolean;
}

const TimeSlotPicker = ({ days, selectedTime, onSelectTime, onLoadMore, hasMore = true }: TimeSlotPickerProps) => {
  const [requestedVisibleDayCount, setRequestedVisibleDayCount] = useState(INITIAL_VISIBLE_DAYS);
  const [expandedSlotDays, setExpandedSlotDays] = useState<string[]>([]);
  const dayDates = useMemo(() => new Set(days.map((day) => day.date)), [days]);
  const activeExpandedSlotDays = useMemo(
    () => expandedSlotDays.filter((date) => dayDates.has(date)),
    [dayDates, expandedSlotDays],
  );
  const visibleDayCount =
    days.length === 0 ? 0 : Math.min(Math.max(requestedVisibleDayCount, INITIAL_VISIBLE_DAYS), days.length);

  const visibleDays = days.slice(0, visibleDayCount);
  const hasHiddenLoadedDays = visibleDayCount < days.length;

  return (
    <div className="space-y-6">
      {visibleDays.map((day) => {
        const availableSlots = day.slots.filter((s) => s.available);
        const fewSlots = availableSlots.length > 0 && availableSlots.length <= 3;
        const isSlotListExpanded = activeExpandedSlotDays.includes(day.date);
        const visibleSlots = isSlotListExpanded ? availableSlots : availableSlots.slice(0, INITIAL_VISIBLE_SLOTS);

        return (
          <div key={day.date} className="animate-fade-in-up">
            <div className="mb-3 flex items-baseline gap-2">
              <h3 className="text-foreground text-lg font-bold">{day.label}</h3>
              <span className="text-muted-foreground text-sm">{day.date}</span>
              {fewSlots && <span className="text-warning ml-auto text-xs font-medium">Poucos horários</span>}
            </div>

            {availableSlots.length === 0 ? (
              <p className="bg-muted text-muted-foreground rounded-xl py-4 text-center text-sm">
                Sem horários disponíveis
              </p>
            ) : (
              <>
                <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 md:grid-cols-5">
                  {visibleSlots.map((slot) => {
                    const isSelected = selectedTime?.date === day.date && selectedTime?.time === slot.time;

                    return (
                      <button
                        type="button"
                        key={slot.time}
                        disabled={!slot.available}
                        onClick={() => onSelectTime(day.date, slot.time, day.label)}
                        className={`h-12 rounded-xl text-sm font-semibold transition-all duration-150 ${
                          isSelected
                            ? 'bg-primary text-primary-foreground scale-[1.03] shadow-md'
                            : slot.available
                              ? 'border-border bg-card text-card-foreground hover:border-primary hover:text-primary border hover:shadow-sm active:scale-95'
                              : 'bg-muted text-muted-foreground/40 cursor-not-allowed line-through'
                        }`}
                      >
                        {slot.time}
                      </button>
                    );
                  })}
                </div>

                {availableSlots.length > INITIAL_VISIBLE_SLOTS && (
                  <button
                    type="button"
                    onClick={() =>
                      setExpandedSlotDays((current) =>
                        current.includes(day.date)
                          ? current.filter((date) => date !== day.date)
                          : [...current, day.date],
                      )
                    }
                    className="text-primary hover:text-primary/80 mt-3 flex items-center gap-2 text-sm font-medium transition-colors"
                  >
                    <span>{isSlotListExpanded ? 'Ver menos horários' : 'Ver mais horários'}</span>
                    <ChevronDown className={`h-4 w-4 transition-transform ${isSlotListExpanded ? 'rotate-180' : ''}`} />
                  </button>
                )}
              </>
            )}
          </div>
        );
      })}

      {(hasHiddenLoadedDays || hasMore) && (
        <button
          type="button"
          onClick={() => {
            const nextVisibleDayCount = requestedVisibleDayCount + INITIAL_VISIBLE_DAYS;
            const shouldLoadMoreDays = hasMore && nextVisibleDayCount > days.length;

            setRequestedVisibleDayCount(nextVisibleDayCount);

            if (shouldLoadMoreDays) {
              onLoadMore?.();
            }
          }}
          className="border-border bg-card text-foreground hover:bg-muted flex h-12 w-full items-center justify-center gap-2 rounded-xl border font-medium transition-colors"
        >
          <span>Ver mais dias</span>
          <ChevronDown className="h-4 w-4" />
        </button>
      )}
    </div>
  );
};

export default TimeSlotPicker;
